import { totpCodeSchema, type TwoFactorSetup } from '@fluxus/shared';
import { Copy, Download, Share2 } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { isShareCanceled, saveTextFile, textFileDelivery } from '@/lib/platform';

/**
 * Peças da ativação da verificação em duas etapas, usadas em Minha conta e na ativação obrigatória
 * durante o login: QR code + chave + primeiro código, e os códigos de recuperação.
 */

/** Passo 1: QR code (e a chave para digitar) e o primeiro código do app. */
export function TotpSetupForm({
  setup,
  account,
  activate,
  onCancel,
  onEnabled,
  cancelLabel = 'Cancelar',
}: {
  setup: TwoFactorSetup;
  /** E-mail da conta (texto alternativo do QR code). */
  account: string;
  /** Confirma o código e devolve os códigos de recuperação. */
  activate: (code: string) => Promise<string[]>;
  onCancel: () => void;
  onEnabled: (codes: string[]) => void;
  cancelLabel?: string;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [qrError, setQrError] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { code: useId() };

  useEffect(() => {
    let active = true;
    // Preto no branco, com margem: o contraste que as câmeras leem melhor, em qualquer tema. SVG
    // (vetor, sem canvas): nítido em qualquer densidade de tela.
    QRCode.toString(setup.otpauthUri, {
      type: 'svg',
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((svg) => active && setQr(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`))
      .catch(() => active && setQrError(true));
    return () => {
      active = false;
    };
  }, [setup.otpauthUri]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = totpCodeSchema.safeParse(code);
    if (!parsed.success) return setError(parsed.error.issues[0]?.message);
    setError(undefined);
    setSaving(true);
    try {
      onEnabled(await activate(parsed.data));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_CODE') setError(err.message);
      else setFormError(err);
      setCode('');
    } finally {
      setSaving(false);
    }
  }

  const key = setup.secret.match(/.{1,4}/g)?.join(' ') ?? setup.secret;

  return (
    <section aria-label="Ativar" className="rounded-lg border border-border bg-surface p-5">
      <h2 className="text-base font-bold">Ative em três passos</h2>
      <ol className="mt-4 flex flex-col gap-5 text-sm">
        <li>
          <p className="font-semibold">1. Instale um app autenticador no celular</p>
          <p className="mt-1 text-muted-foreground">
            Google Authenticator, Microsoft Authenticator, 1Password ou outro app compatível com códigos TOTP.
          </p>
        </li>
        <li>
          <p className="font-semibold">2. No app, leia o QR code (ou digite a chave)</p>
          <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex size-52 shrink-0 items-center justify-center rounded-lg border border-border bg-white">
              {qr ? (
                <img
                  src={qr}
                  alt={`QR code para cadastrar ${account} no app autenticador`}
                  className="size-52"
                />
              ) : qrError ? (
                <p className="p-4 text-center text-xs text-neutral-700">
                  Não foi possível desenhar o QR code. Use a chave.
                </p>
              ) : (
                <span className="text-xs text-neutral-700">Gerando QR code…</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-muted-foreground">Chave para digitar:</p>
              <p className="mt-1 font-mono text-base break-all" aria-label="Chave do app autenticador">
                {key}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(setup.secret)
                    .then(() => toast.success('Chave copiada'))
                    .catch(() => toast.error('Não foi possível copiar. Selecione e copie a chave.'))
                }
              >
                <Copy aria-hidden />
                Copiar chave
              </Button>
            </div>
          </div>
        </li>
        <li>
          <p className="font-semibold">3. Digite o código que aparece no app</p>
          <form noValidate onSubmit={submit} className="mt-3 flex flex-col gap-4 sm:max-w-xs">
            <FormField id={ids.code} label="Código de 6 números" error={error}>
              <Input
                {...fieldA11y(ids.code, error)}
                value={code}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                placeholder="123456"
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(undefined);
                }}
                className="h-12 text-center font-mono text-lg tracking-[0.3em]"
              />
            </FormField>
            {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
                {cancelLabel}
              </Button>
              <Button type="submit" loading={saving}>
                Ativar
              </Button>
            </div>
          </form>
        </li>
      </ol>
    </section>
  );
}

/** Passo 2: os códigos de recuperação, mostrados uma única vez. */
export function RecoveryCodesView({
  codes,
  onDone,
  doneLabel = 'Concluir',
}: {
  codes: string[];
  onDone: () => void;
  doneLabel?: string;
}) {
  const [saved, setSaved] = useState(false);
  const text = [
    'Fluxus BSC — códigos de recuperação da verificação em duas etapas',
    'Cada código vale uma vez. Guarde em lugar seguro.',
    '',
    ...codes,
  ].join('\n');

  const share = textFileDelivery() === 'share';
  function save() {
    saveTextFile({
      fileName: 'fluxus-codigos-de-recuperacao.txt',
      title: 'Códigos de recuperação do Fluxus BSC',
      text,
    }).catch((err: unknown) => {
      // Fechar o compartilhar sem escolher nada não é erro.
      if (isShareCanceled(err)) return;
      toast.error('Não foi possível salvar o arquivo', {
        description: 'Copie os códigos e guarde-os em lugar seguro.',
      });
    });
  }

  return (
    <section aria-label="Códigos de recuperação" className="rounded-lg border border-border bg-surface p-5">
      <h2 className="text-base font-bold">Guarde seus códigos de recuperação</h2>
      <p className="mt-2 max-w-prose text-sm text-muted-foreground">
        Se perder o celular, use um destes códigos no lugar do código do app. Cada um vale uma vez. Eles não
        serão mostrados de novo.
      </p>
      <ul
        aria-label="Códigos"
        className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-muted p-4 font-mono text-sm sm:grid-cols-5"
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button
          variant="outline"
          onClick={() =>
            void navigator.clipboard
              .writeText(codes.join('\n'))
              .then(() => toast.success('Códigos copiados'))
              .catch(() => toast.error('Não foi possível copiar. Baixe o arquivo.'))
          }
        >
          <Copy aria-hidden />
          Copiar códigos
        </Button>
        <Button variant="outline" onClick={save}>
          {share ? <Share2 aria-hidden /> : <Download aria-hidden />}
          {share ? 'Salvar ou enviar' : 'Baixar arquivo .txt'}
        </Button>
      </div>
      <label className="mt-5 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-(--brand)"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
        />
        Guardei os códigos em um lugar seguro
      </label>
      <Button className="mt-4" disabled={!saved} onClick={onDone}>
        {doneLabel}
      </Button>
    </section>
  );
}
